import { Form, Input, InputNumber, Modal, Select } from "antd";
import { useEffect, useState } from "react";
import { addManualInventory } from "../../apis/inventory.api";
import { fetchVendors, getVendor } from "../../apis/vendor.api";

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

interface FormValues {
  variant_handle: string;
  vendor_id: number;
  warehouse_id: number;
  quantity: number;
  batch_no?: string;
  remark?: string;
}

// Adds a hand-entered stock row. The Zoho sync never removes these rows,
// so they stay until someone edits or deletes them on the Inventory page.
export default function AddManualInventoryModal({ open, onClose, onAdded }: Props) {
  const [form] = Form.useForm<FormValues>();
  const [vendors, setVendors] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && vendors.length === 0) {
      fetchVendors({}).then(setVendors);
    }
  }, [open]);

  const onVendorChange = async (vendorId: number) => {
    form.setFieldValue("warehouse_id", undefined);
    setWarehouses([]);
    setLoadingWarehouses(true);
    const vendor = await getVendor(vendorId);
    setWarehouses(vendor?.warehouses ?? []);
    setLoadingWarehouses(false);
  };

  const handleClose = () => {
    form.resetFields();
    setWarehouses([]);
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const row = await addManualInventory({
      variant_handle: values.variant_handle.trim(),
      warehouse_id: values.warehouse_id,
      quantity: values.quantity,
      batch_no: values.batch_no?.trim() || "",
      remark: values.remark?.trim() || "",
    });
    setSaving(false);
    if (row) {
      handleClose();
      onAdded();
    }
  };

  return (
    <Modal
      title="Add Inventory Manually"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Add"
      confirmLoading={saving}
      destroyOnClose
    >
      <p className="text-muted mb-3">
        Sync doesn't remove manually added rows. Edit or delete them here when the
        stock changes.
      </p>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Variant Handle"
          name="variant_handle"
          rules={[{ required: true, whitespace: true, message: "Enter the variant handle" }]}
        >
          <Input placeholder="e.g. md-100-oak-laminate" />
        </Form.Item>
        <Form.Item
          label="Vendor"
          name="vendor_id"
          rules={[{ required: true, message: "Select a vendor" }]}
        >
          <Select
            showSearch
            placeholder="Select vendor"
            optionFilterProp="label"
            onChange={onVendorChange}
            options={vendors.map((v) => ({
              value: v.id,
              label: `${v.name}${v.vendor_code ? ` (${v.vendor_code})` : ""}`,
            }))}
          />
        </Form.Item>
        <Form.Item
          label="Warehouse"
          name="warehouse_id"
          rules={[{ required: true, message: "Select a warehouse" }]}
        >
          <Select
            showSearch
            placeholder="Select warehouse"
            optionFilterProp="label"
            loading={loadingWarehouses}
            disabled={warehouses.length === 0}
            options={warehouses.map((w) => ({
              value: w.id,
              label: [w.name, w.city].filter(Boolean).join(", ") || `Warehouse ${w.id}`,
            }))}
          />
        </Form.Item>
        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[{ required: true, message: "Enter the quantity" }]}
        >
          <InputNumber min={0} step={1} precision={2} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Batch No" name="batch_no">
          <Input placeholder="Optional" />
        </Form.Item>
        <Form.Item label="Remark" name="remark">
          <Input.TextArea rows={2} placeholder="Optional — why this stock is added by hand" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
